import { Box, HStack, Text, VStack } from "@chakra-ui/react";
import type { AdPreviewCollaboration } from "../../utils/adPreviewCollaboration";
import { formatAdPreviewUsername } from "../../utils/adPreviewCollaboration";
import { ProfileAvatar } from "./ProfileAvatar";

export type CollaborationIdentityDisplayProps = {
  platform: "instagram" | "facebook";
  tone?: "light" | "dark";
  size?: "sm" | "md" | "lg";
  primaryName: string;
  primaryProfilePictureUrl?: string;
  collaboration?: AdPreviewCollaboration;
  /** Shown under the title when there is no partnership subtitle (e.g. story “Sponsored”). */
  secondaryLabel?: string;
};

const sizeMap = {
  sm: { avatar: "28px", fontSize: "xs", subFontSize: "2xs" },
  md: { avatar: "32px", fontSize: "sm", subFontSize: "xs" },
  lg: { avatar: "40px", fontSize: "sm", subFontSize: "xs" },
} as const;

export default function CollaborationIdentityDisplay({
  platform,
  tone = "light",
  size = "md",
  primaryName,
  primaryProfilePictureUrl,
  collaboration,
  secondaryLabel,
}: CollaborationIdentityDisplayProps) {
  const metrics = sizeMap[size];
  const primaryLabel = formatAdPreviewUsername(primaryName);
  const instagramPartnerLabel = collaboration?.partnerInstagramUsername
    ? formatAdPreviewUsername(collaboration.partnerInstagramUsername)
    : undefined;
  const facebookPartnerLabel =
    collaboration?.partnerFacebookPageName?.trim() ||
    instagramPartnerLabel;
  const isDark = tone === "dark";
  const primaryColor = isDark ? "white" : "black";
  const secondaryColor = isDark ? "whiteAlpha.800" : "gray.600";

  const instagramTitle =
    instagramPartnerLabel != null
      ? `${primaryLabel} and ${instagramPartnerLabel}`
      : primaryLabel;

  const facebookTitle =
    facebookPartnerLabel != null
      ? collaboration?.partnerFacebookPageName
        ? `${primaryLabel} with ${collaboration.partnerFacebookPageName}`
        : `${primaryLabel} with @${facebookPartnerLabel}`
      : primaryLabel;

  const title = platform === "instagram" ? instagramTitle : facebookTitle;
  const showPartnerAvatar =
    platform === "instagram"
      ? Boolean(instagramPartnerLabel)
      : Boolean(facebookPartnerLabel);
  const partnerAvatarName =
    platform === "instagram"
      ? instagramPartnerLabel ?? "Partner"
      : facebookPartnerLabel ?? "Partner";
  const partnerPictureUrl =
    platform === "instagram"
      ? collaboration?.partnerInstagramProfilePictureUrl
      : collaboration?.partnerFacebookPagePictureUrl ||
        collaboration?.partnerInstagramProfilePictureUrl;
  const showPaidPartnership =
    platform === "instagram"
      ? Boolean(instagramPartnerLabel)
      : Boolean(facebookPartnerLabel);

  return (
    <HStack spacing={3} alignItems="center" flex={1} minW={0}>
      <HStack spacing={0} flexShrink={0}>
        <ProfileAvatar
          platform={platform}
          name={primaryLabel}
          pictureUrl={primaryProfilePictureUrl}
          size={metrics.avatar}
        />
        {collaboration && showPartnerAvatar ? (
          <Box ml="-10px">
            <ProfileAvatar
              platform={platform}
              name={partnerAvatarName}
              pictureUrl={partnerPictureUrl}
              size={metrics.avatar}
            />
          </Box>
        ) : null}
      </HStack>
      <VStack spacing={0} alignItems="flex-start" flex={1} minW={0}>
        <Text
          fontSize={metrics.fontSize}
          fontWeight="semibold"
          color={primaryColor}
          noOfLines={2}
          lineHeight="1.3"
        >
          {title}
        </Text>
        {showPaidPartnership ? (
          <Text
            fontSize={metrics.subFontSize}
            color={secondaryColor}
            noOfLines={1}
          >
            Paid partnership
          </Text>
        ) : secondaryLabel ? (
          <Text
            fontSize={metrics.subFontSize}
            color={secondaryColor}
            noOfLines={1}
          >
            {secondaryLabel}
          </Text>
        ) : null}
      </VStack>
    </HStack>
  );
}
